import type { ConfigEnvironment, ConfigInput, HostOptions } from '../contracts';
import { mergeAppProfileDefaults } from './appProfileConfig.ts';

function readConfigValue(source: object, key: string, fallback = '') {
  const value = (source as ConfigEnvironment)?.[key];
  if (value === undefined || value === null) {
    return fallback;
  }
  return String(value).trim();
}

function resolveOrigin({ locationOrigin = '', fallbackOrigin = '' } = {}) {
  return String(locationOrigin || fallbackOrigin || '').trim();
}

function configArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function configObject(value: unknown): ConfigEnvironment | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as ConfigEnvironment : null;
}

function parseJsonConfig(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readJsonArrayConfig<T>(source: object, key: string, fallback: T[] = []): T[] {
  const value = (source as ConfigEnvironment)?.[key];
  if (Array.isArray(value)) {
    return value as T[];
  }
  if (typeof value !== 'string' || !value.trim()) {
    return configArray<T>(fallback);
  }
  return configArray<T>(parseJsonConfig(value));
}

function readObjectConfig<T extends object>(source: object, key: string, fallback: object | null = {}): T {
  const value = (source as ConfigEnvironment)?.[key];
  const record = configObject(value);
  if (record) {
    return record as T;
  }
  if (typeof value === 'string' && value.trim()) {
    return (configObject(parseJsonConfig(value)) || {}) as T;
  }
  return (configObject(fallback) || {}) as T;
}

function readBooleanConfig(source: object, key: string, fallback = false) {
  const value = (source as ConfigEnvironment)?.[key];
  if (value === undefined || value === null || value === '') {
    return Boolean(fallback);
  }
  if (typeof value === 'boolean') {
    return value;
  }
  const text = String(value).trim().toLowerCase();
  return ['1', 'true', 'yes', 'on'].includes(text);
}

function resolveUiLabels(defaults: HostOptions = {}) {
  return configObject(defaults.uiLabels) || configObject(defaults.instanceProfile?.ui_labels) || {};
}

function resolveDevToolbarConfig(env: ConfigEnvironment, resolvedDefaults: NonNullable<ConfigInput['defaults']>) {
  return {
    enabled: readBooleanConfig(
      env,
      'VITE_CHAT_DEV_TOOLBAR',
      resolvedDefaults.devToolbarEnabled,
    ),
    fixtures: readJsonArrayConfig(
      env,
      'VITE_CHAT_DEV_FIXTURES_JSON',
      resolvedDefaults.devToolbarFixtures,
    ),
    instances: readJsonArrayConfig(
      env,
      'VITE_CHAT_DEV_INSTANCES_JSON',
      resolvedDefaults.devToolbarInstances,
    ),
    backendOptions: readJsonArrayConfig(
      env,
      'VITE_CHAT_DEV_BACKEND_OPTIONS_JSON',
      resolvedDefaults.devToolbarBackendOptions,
    ),
    backendSwitchParam: readConfigValue(
      env,
      'VITE_CHAT_DEV_BACKEND_SWITCH_PARAM',
      resolvedDefaults.devToolbarBackendSwitchParam || 'flare_backend',
    ),
    selectedInstanceId: readConfigValue(
      env,
      'VITE_CHAT_DEV_SELECTED_INSTANCE_ID',
      resolvedDefaults.devToolbarSelectedInstanceId || resolvedDefaults.instanceId,
    ),
    switchParam: readConfigValue(
      env,
      'VITE_CHAT_DEV_INSTANCE_SWITCH_PARAM',
      resolvedDefaults.devToolbarSwitchParam || 'flare_instance',
    ),
  };
}

export function resolveInstanceAppConfig({
  env = {},
  defaults = {},
  appProfile = {},
  locationOrigin = '',
}: ConfigInput = {}): HostOptions {
  const resolvedDefaults = mergeAppProfileDefaults(appProfile, defaults);
  const apiBaseUrl = readConfigValue(
    env,
    'VITE_CHAT_API_BASE_URL',
    resolveOrigin({
      locationOrigin,
      fallbackOrigin: resolvedDefaults.apiBaseUrl,
    }),
  );
  const kernelBaseUrl = readConfigValue(
    env,
    'VITE_CHAT_KERNEL_BASE_URL',
    resolvedDefaults.kernelBaseUrl || apiBaseUrl,
  );

  return {
    functionType: readConfigValue(env, 'VITE_CHAT_FUNCTION_TYPE', resolvedDefaults.functionType),
    defaultSessionTitle: readConfigValue(
      env,
      'VITE_CHAT_DEFAULT_SESSION_TITLE',
      resolvedDefaults.defaultSessionTitle,
    ),
    projectId: readConfigValue(env, 'VITE_CHAT_PROJECT_ID', resolvedDefaults.projectId),
    userId: readConfigValue(env, 'VITE_CHAT_USER_ID', resolvedDefaults.userId),
    backendMode: readConfigValue(
      env,
      'VITE_CHAT_DEV_DEFAULT_BACKEND',
      resolvedDefaults.backendMode || 'real',
    ).toLowerCase(),
    kernelBaseUrl,
    apiBaseUrl,
    apiToken: readConfigValue(env, 'VITE_CHAT_API_TOKEN', resolvedDefaults.apiToken),
    defaultProjectName: readConfigValue(
      env,
      'VITE_CHAT_DEFAULT_PROJECT_NAME',
      resolvedDefaults.defaultProjectName,
    ),
    instanceId: readConfigValue(env, 'VITE_CHAT_INSTANCE_ID', resolvedDefaults.instanceId),
    domainPackDomain: readConfigValue(
      env,
      'VITE_CHAT_DOMAIN_PACK_DOMAIN',
      resolvedDefaults.domainPackDomain,
    ),
    domainPackVersion: readConfigValue(
      env,
      'VITE_CHAT_DOMAIN_PACK_VERSION',
      resolvedDefaults.domainPackVersion,
    ),
    starterScenarios: readJsonArrayConfig(
      env,
      'VITE_CHAT_STARTER_SCENARIOS_JSON',
      resolvedDefaults.starterScenarios,
    ),
    composerModeOptions: readJsonArrayConfig(
      env,
      'VITE_CHAT_COMPOSER_MODE_OPTIONS_JSON',
      resolvedDefaults.composerModeOptions,
    ),
    capabilityCatalog: readJsonArrayConfig(
      env,
      'VITE_CHAT_CAPABILITY_CATALOG_JSON',
      resolvedDefaults.capabilityCatalog,
    ),
    modulePromptRegistry: readJsonArrayConfig(
      env,
      'VITE_CHAT_MODULE_PROMPT_REGISTRY_JSON',
      resolvedDefaults.modulePromptRegistry,
    ),
    branding: readObjectConfig(
      env,
      'VITE_CHAT_BRANDING_JSON',
      readObjectConfig(resolvedDefaults, 'branding'),
    ),
    instanceProfile: readObjectConfig(
      env,
      'VITE_CHAT_INSTANCE_PROFILE_JSON',
      readObjectConfig(resolvedDefaults, 'instanceProfile', null),
    ),
    displayPolicy: readObjectConfig(
      env,
      'VITE_CHAT_DISPLAY_POLICY_JSON',
      readObjectConfig(resolvedDefaults, 'displayPolicy'),
    ),
    uiLabels: readObjectConfig(
      env,
      'VITE_CHAT_UI_LABELS_JSON',
      resolveUiLabels(resolvedDefaults),
    ),
    devToolbar: resolveDevToolbarConfig(env, resolvedDefaults),
  };
}
